/**
 * Which role opens which part of the app — the client's copy of the route
 * gates, used to hide what the server would refuse. The server stays the
 * authority; this only decides what is drawn.
 *
 * Pure so it can be mutation-tested; the hooks that feed it the role of the
 * OPEN workspace live in `hooks/useCanAccess.ts`.
 */
import { RoleHierarchy, type UserRole } from '@bakedshift/shared';

export const FEATURES = [
  'dashboard', 'transactions', 'invoices', 'employees', 'timesheets',
  'billers', 'taxes', 'chat', 'auditLog', 'settings',
] as const;
export type Feature = typeof FEATURES[number];

const MIN_ROLE: Record<Feature, UserRole> = {
  dashboard: 'accountant',
  transactions: 'accountant',
  invoices: 'accountant',
  employees: 'manager',
  timesheets: 'employee',
  billers: 'accountant',
  taxes: 'accountant',
  chat: 'accountant',
  auditLog: 'accountant',
  // Settings is the workspace admin's alone (decision 2026-09-25).
  settings: 'workspace-admin',
};

export function canAccessFeature(role: UserRole | null, feature: Feature): boolean {
  if (!role || !Object.hasOwn(MIN_ROLE, feature)) return false;
  // Every MIN_ROLE is in the hierarchy, so an unknown role (-1) is below all of them.
  return RoleHierarchy.indexOf(role) >= RoleHierarchy.indexOf(MIN_ROLE[feature]);
}

export function canEditSettings(role: UserRole | null): boolean {
  return role === 'workspace-admin';
}

/**
 * Where "home" is for a role. Every "go to dashboard" in the app — sign-in,
 * the landing route, a workspace switch, the error pages — targets
 * `/dashboard`, and the dashboard route sends a role that cannot open it here.
 * An unresolved role keeps the dashboard: nothing is known to refuse it yet.
 */
export function homePathFor(role: UserRole | null): string {
  return role === null || canAccessFeature(role, 'dashboard') ? '/dashboard' : '/timesheets';
}
